import React from 'react';
import {ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import {chartHeights, chartWidths} from '@/views/storyboard/utils/chartConstants';
import i18n from 'i18n';

interface Props {
    chartSettingsFormik: FormikT<ChartSettings>;
}

interface SegmentedControlOption {
    value: number;
    label: string;
}

interface SegmentedControlProps {
    value: number;
    options: SegmentedControlOption[];
    onChange: (value: number) => void;
    ariaLabel: string;
}

export const ChartSettingsSegmentedControl = React.memo<SegmentedControlProps>(({
    value,
    options,
    onChange,
    ariaLabel,
}) => (
    <div className="chart-settings-size-card__segmented" role="group" aria-label={ariaLabel}>
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                className={`chart-settings-size-card__option${value === option.value ? ' chart-settings-size-card__option--selected' : ''}`}
                onClick={() => onChange(option.value)}
            >
                {option.label}
            </button>
        ))}
    </div>
));

interface BooleanSegmentedControlProps {
    value: boolean;
    onChange: (value: boolean) => void;
    ariaLabel: string;
}

export const ChartSettingsBooleanSegmentedControl = React.memo<BooleanSegmentedControlProps>(({
    value,
    onChange,
    ariaLabel,
}) => (
    <div className="chart-settings-size-card__segmented" role="group" aria-label={ariaLabel}>
        <button
            type="button"
            className={`chart-settings-size-card__option${!value ? ' chart-settings-size-card__option--selected' : ''}`}
            onClick={() => onChange(false)}
        >
            {i18n.t('chart_settings_option_off.text')}
        </button>
        <button
            type="button"
            className={`chart-settings-size-card__option${value ? ' chart-settings-size-card__option--selected' : ''}`}
            onClick={() => onChange(true)}
        >
            {i18n.t('chart_settings_option_on.text')}
        </button>
    </div>
));

export const ChartSettingsSizeControls = React.memo<Props>(({chartSettingsFormik}) => {
    const widthOptions = chartWidths.map(([value, label]) => ({
        value,
        label: value === 4 ? i18n.t('chart_settings_size_full.text') : label,
    }));
    const heightLabels = ['Tiny', 'S', 'M', 'L', 'XL'];
    const heightOptions = chartHeights.map(([value, label], index) => ({
        value,
        label: heightLabels[index] || label,
    }));

    return (
        <div className="chart-settings-size-card">
            <div className="chart-settings-size-card__group">
                <div className="chart-settings-size-card__label">
                    {i18n.t('chart_settings_width.text')}
                </div>
                <ChartSettingsSegmentedControl
                    value={chartSettingsFormik.values.width}
                    options={widthOptions}
                    onChange={(value) => chartSettingsFormik.setFieldValue('width', value)}
                    ariaLabel={i18n.t('chart_settings_width.text')}
                />
            </div>

            <div className="chart-settings-size-card__group">
                <div className="chart-settings-size-card__label">
                    {i18n.t('chart_settings_height.text')}
                </div>
                <ChartSettingsSegmentedControl
                    value={chartSettingsFormik.values.height}
                    options={heightOptions}
                    onChange={(value) => chartSettingsFormik.setFieldValue('height', value)}
                    ariaLabel={i18n.t('chart_settings_height.text')}
                />
            </div>
        </div>
    );
});
